import { useContext } from "react";
import { useTranslation } from "react-i18next";
import FileDownloadedNotification from "./FileDownloadedNotification";
import InvoiceApi from "../../../../../../../../api/invoices/invoiceApi";
import useInvoiceApi from "../../../../../../../../api/invoices/useInvoiceApi";
import JobsServiceApi from "../../../../../../../../api/jobsService/jobsServiceApi";
import useJobServiceApi from "../../../../../../../../api/jobsService/useJobServiceApi";
import { ConfigContext } from "../../../../../../../../context/ConfigContext";
import { FeatureToggleContextV2 } from "../../../../../../../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { PreferenceContext } from "../../../../../../../../context/PreferencesContext/PreferencesContext";
import {
  CATEGORIES,
  TOGGLES,
} from "../../../../../../../../pages/featureToggles/constants";
import {
  INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION,
  INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION,
  INVOICE_CONSOLIDATION,
  INVOICE_FILTER,
} from "../../../../../../../../utils/constants";
import { INV_FILE_PREFIX } from "../../../../../../../../utils/eventConstants";
import {
  formatDate,
  formatDateAsYYYYMMDD,
  formatTime,
} from "../../../../../../../../utils/formatters";
import { Notification } from "../../hooks/useNotifications/useNotifications";

interface ConsolidateInvoiceNotificationProps {
  notification: Notification;
  accountId: string;
  expireNotification: (notificationId: number) => void;
  downloadFile: (url: string, fileName: string) => void;
}

const ConsolidatedInvoicesNotification = ({
  notification,
  accountId,
  expireNotification,
  downloadFile,
}: ConsolidateInvoiceNotificationProps) => {
  let generateDownloadLink;
  let outputFileName;
  let successMessage;
  let failureMessage;
  let partialFailureMessage;
  let subMessage;
  const { t, i18n } = useTranslation("common");
  const {
    INVOICE_SERVICE_URL,
    JOBS_SERVICE_URL,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_JOBS_SERVICE,
    ENABLE_CONSOLIDATION_METHOD_PREFERENCE,
  } = useContext(ConfigContext);
  const { isToggleEnabled } = useContext(FeatureToggleContextV2);
  const { userPreferences } = useContext(PreferenceContext);
  const consolidationMethodPreference = userPreferences?.find(
    (preference) => preference.key === "consolidation_method"
  )?.value;
  const { downloadInvoices } = useInvoiceApi();
  const { payload, createdAt } = notification;
  const isAppNotificationsEnabled = isToggleEnabled(
    CATEGORIES.ALERTS_SERVICE,
    TOGGLES.ENABLE_ALERTS_AND_NOTIFICATIONS
  );

  const { downloadJob } = useJobServiceApi();

  const isJobsApiAuthEnable =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_JOBS_SERVICE;

  const invoiceFileHref =
    (jobId: string, fileName: string) => async (accessToken: string) => {
      const fileResponse = ENABLE_RBAC_AUTH_SUPPORT
        ? await downloadInvoices(accountId, jobId)
        : await InvoiceApi.downloadInvoices(
            INVOICE_SERVICE_URL,
            accountId,
            jobId,
            accessToken
          );

      downloadFile(URL.createObjectURL(fileResponse.data), fileName);
    };

  const downloadConsolidatedInvoices =
    (jobId: string, fileName: string) => async (accessToken: string) => {
      const fileResponse = isJobsApiAuthEnable
        ? await downloadJob(jobId, accountId, fileName, i18n.language)
        : await JobsServiceApi.downloadJob(
            JOBS_SERVICE_URL,
            jobId,
            accountId,
            fileName,
            accessToken,
            i18n.language
          );

      downloadFile(URL.createObjectURL(fileResponse.data), fileName);
    };

  const singleAccountInvoiceConsolidation =
    payload.type === INVOICE_CONSOLIDATION || payload.type === INVOICE_FILTER;
  const multipleAccountInvoiceConsolidation =
    payload.type === INVOICE_BY_ACCOUNT_NUMBERS_CONSOLIDATION ||
    payload.type === INVOICE_BY_ACCOUNT_FILTER_CONSOLIDATION;

  const date = formatDate(createdAt.toString(), i18n.language);
  const time = formatTime(createdAt.toString(), i18n.language);

  if (singleAccountInvoiceConsolidation) {
    successMessage = t("notifications.consolidateInvoices.success.v2", {
      date,
      time,
    });
    failureMessage = t("notifications.consolidateInvoices.failure.v2", {
      date,
      time,
    });

    generateDownloadLink = downloadConsolidatedInvoices(
      payload.id,
      `${payload.filename}.pdf`
    );
  } else if (multipleAccountInvoiceConsolidation) {
    const failedAccountIds: string[] = payload.metadata.error
      ? payload.metadata.error.map(
          (errorMessage: any) => errorMessage.accountId
        )
      : [];

    partialFailureMessage = t(
      "notifications.consolidateInvoices.partialSuccess",
      { date, time }
    );

    if (failedAccountIds.length > 0) {
      subMessage = t(
        "notifications.consolidateInvoices.partialSuccessSubMessage",
        { failedAccountIds }
      );
    }

    if (isAppNotificationsEnabled) {
      successMessage = t("notifications.consolidateInvoices.success.v2", {
        date,
        time,
      });
      failureMessage =
        failedAccountIds.length > 0
          ? t("notifications.consolidateInvoices.partialSuccess.v2", {
              date,
              time,
              failedAccountIds,
            })
          : t("notifications.consolidateInvoices.failure.v2", { date, time });
    } else {
      successMessage =
        failedAccountIds.length > 0
          ? partialFailureMessage
          : t("notifications.consolidateInvoices.success.v2", {
              date,
              time,
            });

      failureMessage = t("notifications.consolidateInvoices.failure.v2", {
        date,
        time,
      });
    }

    generateDownloadLink = downloadConsolidatedInvoices(
      payload.id,
      `${payload.filename}.${
        ENABLE_CONSOLIDATION_METHOD_PREFERENCE
          ? consolidationMethodPreference
          : "zip"
      }`
    );
  } else {
    const fileNameParts = payload.filename.split("_");
    outputFileName = `${INV_FILE_PREFIX}_${formatDateAsYYYYMMDD(
      fileNameParts[1]
    )}_${formatDateAsYYYYMMDD(fileNameParts[2])}`;

    const startDate = formatDate(parseInt(fileNameParts[1], 10), i18n.language);

    const endDate = formatDate(parseInt(fileNameParts[2], 10), i18n.language);
    successMessage = t("notifications.consolidateInvoices.success", {
      startDate,
      endDate,
    });
    failureMessage = t("notifications.consolidateInvoices.failure", {
      startDate,
      endDate,
    });

    generateDownloadLink = invoiceFileHref(payload.id, outputFileName);
  }

  return (
    <FileDownloadedNotification
      notification={notification}
      expireNotification={() =>
        expireNotification(new Date(notification.createdAt).getTime())
      }
      successMessage={successMessage}
      failureMessage={failureMessage}
      downloadFunction={generateDownloadLink}
      subMessage={subMessage}
    />
  );
};

export default ConsolidatedInvoicesNotification;
